import { useMemo } from 'react';
import { useGetPractitionerSettings } from 'src/hooks/settings/useGetPractitionerSettings';
import { EncounterSummaryVerbosity } from 'src/models/encounter';
import { PractitionerSettings } from 'src/models/practitioner';

type UseResolvedVerbosityParams = {
  practitioner_id?: number;
  encounter_verbosity?: EncounterSummaryVerbosity | null;
  currentPractitionerSettings?: PractitionerSettings | null;
  currentPractitionerId?: number;
};

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Resolves the verbosity for an encounter by preferring the practitioner's
 * current default over the stale value baked into the encounter at creation.
 *
 * - Own encounters: uses Redux settings directly (zero extra API calls).
 * - Other practitioner's encounters: fetches via React Query with a 5-min
 *   staleTime so repeated clicks reuse the cache.
 *
 * Resolution order:
 *   1. Fresh practitioner settings (React Query, only for other practitioners)
 *   2. Redux currentPractitionerSettings (synchronous)
 *   3. Encounter's stored verbosity (fallback)
 */
export const useResolvedVerbosity = ({
  practitioner_id,
  encounter_verbosity,
  currentPractitionerSettings,
  currentPractitionerId,
}: UseResolvedVerbosityParams) => {
  const isOwnEncounter =
    !!currentPractitionerId &&
    !!practitioner_id &&
    currentPractitionerId === practitioner_id;

  const { data: fetchedSettings, isLoading } = useGetPractitionerSettings(
    { practitioner_id },
    {
      enabled: !!practitioner_id && !isOwnEncounter,
      staleTime: FIVE_MINUTES,
    }
  );

  const practitionerSettings = isOwnEncounter
    ? currentPractitionerSettings
    : fetchedSettings ?? currentPractitionerSettings;

  const verbosity = useMemo<EncounterSummaryVerbosity | null>(
    () =>
      (practitionerSettings?.ambient_in_visit_summary_verbosity as EncounterSummaryVerbosity) ??
      encounter_verbosity ??
      null,
    [practitionerSettings, encounter_verbosity]
  );

  return {
    verbosity,
    isLoading: isOwnEncounter ? false : isLoading,
  };
};
